import { useMemo, useState } from 'react'

import { Box, Stack, Typography, IconButton, Tooltip } from '@mui/material'
import { PlusIcon, Trash2Icon, ChevronUpIcon, ChevronDownIcon } from 'lucide-react'

import { Button, Card, Input } from '@repo/ui'
import { checkCustomFieldLink, checkCustomFieldModel, type ProductCustomFieldType } from '@repo/types'
import { RichTextEditorField, SelectField, type SelectItem } from 'components/input/fields'
import { useProductCustomFields } from 'hooks/api'
import type { ProductCustomField } from 'services/productApi'
import { ACTION_BTN_SX, VisibilityToggle } from './fieldVisibilityControls'

interface Row extends ProductCustomField {
  _key: string
  /** Always concrete in local state; a legacy row with no stored type reads as `text`. */
  type: ProductCustomFieldType
}

/**
 * VRG-104 — the three types, with WHERE each one lands on the certificate as the
 * option's description rather than as helper text under the control.
 *
 * `SelectField`'s `description` exists for exactly this case (see its own note): the
 * consequence of picking "Rich text" over "Text" is not in the words "rich text" — it
 * is that one becomes a row in the details table and the other a titled block below the
 * description. A brand picking blind gets their content in a place they did not intend.
 */
const TYPE_OPTIONS: SelectItem[] = [
  {
    value: 'text',
    label: 'Text',
    description: "A row in the certificate's product details, beside Product ID.",
  },
  {
    value: 'rich',
    label: 'Rich text',
    description: 'A titled block below the description. Formatting is kept; links are not.',
  },
  {
    value: 'link',
    label: 'Link',
    description: 'A text link in that block, with the destination host shown beside it.',
  },
  {
    value: 'model',
    label: '3D model',
    description:
      'A rotatable 3D panel in that block. For a second object, below the main one.',
  },
]

const VALUE_PLACEHOLDER: Record<ProductCustomFieldType, string> = {
  text: 'Value',
  rich: '',
  link: 'https://example.com/care',
  model: 'https://example.com/model.glb',
}

/*
 * VRG-813 — `model` says "model file", not "web address".
 *
 * Both are https URLs validated by the same rule, but the label is the only thing on the
 * form that tells a brand WHAT to paste. "Web address" invites a product page; what this
 * field needs is the GLB itself, which is what a product's 3D-model upload returns.
 */
const VALUE_LABEL: Record<ProductCustomFieldType, string> = {
  text: 'Value',
  rich: 'Content',
  link: 'Web address',
  model: 'Model file address',
}

interface ProductCustomFieldsCardProps {
  productId: string
  initial: ProductCustomField[]
}

/**
 * VRG-101 / VRG-104 — curate a product's displayable custom fields. Each field has a
 * human label, a TYPE deciding where it renders, a value, and a visibility toggle.
 * Rows are reorderable; the list saves via its own endpoint (custom_fields is
 * Vera-curated and preserved across re-imports).
 *
 * The row is two lines rather than one. It was one — label and value side by side — and
 * a `type` select plus a rich-text editor do not fit beside them at this card's width,
 * which is the shape §74 warns about: a row that is fine at two controls and clips at
 * four. Splitting it also gives `rich` and `link` the full width they actually need,
 * and keeps the label field the same size whatever the row's type.
 */
export const ProductCustomFieldsCard = ({ productId, initial }: ProductCustomFieldsCardProps) => {
  const { mutate: save, isPending } = useProductCustomFields()

  let seq = 0
  const seed = useMemo(
    () =>
      [...initial]
        .sort((a, b) => a.order - b.order)
        .map((f) => ({ ...f, type: f.type ?? ('text' as const), _key: `cf-${seq++}` })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initial],
  )

  const [rows, setRows] = useState<Row[]>(seed)
  const [snapshot, setSnapshot] = useState(() => serialize(seed))

  const dirty = serialize(rows) !== snapshot

  /**
   * Per-row link problems, from the SHARED rule (`@repo/types`) the API enforces on the
   * write — not a second regex here. A client copy that disagreed with the API would
   * either accept something the save then rejects, or refuse something the save would
   * have taken, and the brand has no way to tell which side is wrong.
   *
   * Only reported once a row has something typed: an empty new link row is incomplete,
   * not invalid, and flagging it the moment it is added reads as the form shouting.
   */
  const linkErrors = useMemo(() => {
    const out: Record<string, string> = {}
    for (const row of rows) {
      // `model` carries the SAME https rule as `link` (the API validates both through
      // one branch), so it gets the same inline feedback. Leaving it out would send a
      // brand's typo to the server and return it as a 400 banner instead of a message
      // under the field that caused it.
      if (row.type !== 'link' && row.type !== 'model') continue
      if (!row.value?.trim()) continue
      // Two DIFFERENT rules, picked by type. A model points at our own storage and so
      // accepts http on a loopback host; a link points at a third party and does not.
      // Sharing one checker here would put the form back out of step with the API.
      const { reason } =
        row.type === 'model'
          ? checkCustomFieldModel(row.value)
          : checkCustomFieldLink(row.value)
      if (reason) out[row._key] = reason
    }
    return out
  }, [rows])

  // A link row saved with no address at all is rejected by the API, so block the Save
  // rather than let it round-trip to a 400 — but only for a row the brand has actually
  // named (an untitled row is dropped on save, see `handleSave`).
  const incompleteLink = rows.some(
    (r) =>
      (r.type === 'link' || r.type === 'model') && r.label.trim() && !r.value?.trim(),
  )
  const blocked = Object.keys(linkErrors).length > 0 || incompleteLink
  const blockedReason = incompleteLink
    ? 'A link field needs a web address before this can be saved.'
    : 'Fix the web address flagged above before saving.'

  const patch = (key: string, p: Partial<Row>) =>
    setRows((prev) => prev.map((r) => (r._key === key ? { ...r, ...p } : r)))

  const remove = (key: string) => setRows((prev) => prev.filter((r) => r._key !== key))

  const move = (index: number, delta: number) =>
    setRows((prev) => {
      const next = [...prev]
      const target = index + delta
      if (target < 0 || target >= next.length) return prev
      ;[next[index], next[target]] = [next[target], next[index]]
      return next
    })

  const add = () =>
    setRows((prev) => [
      ...prev,
      {
        _key: `cf-new-${prev.length}-${Date.now()}`,
        key: '',
        label: '',
        value: '',
        order: prev.length,
        visible: true,
        type: 'text',
      },
    ])

  const handleSave = () => {
    const payload: ProductCustomField[] = rows
      .filter((r) => r.label.trim())
      .map((r, index) => ({
        // New rows have no source key; derive a stable one from the label.
        key: r.key || r.label.trim().toLowerCase().replace(/\s+/g, '_'),
        label: r.label.trim(),
        value: r.value?.trim() || null,
        order: index,
        visible: r.visible,
        type: r.type,
      }))
    save(
      { productId, customFields: payload },
      { onSuccess: () => setSnapshot(serialize(rows)) },
    )
  }

  const renderValueControl = (row: Row) => {
    if (row.type === 'rich') {
      return (
        <RichTextEditorField
          label={VALUE_LABEL.rich}
          value={row.value ?? ''}
          onChange={(html) => patch(row._key, { value: html })}
          // Shorter than the field's 300px default: this is a block on a certificate,
          // not a product description, and a tall editor per row makes a three-field
          // card scroll for no reason.
          minHeight="180px"
        />
      )
    }

    const error = linkErrors[row._key]
    const host = error
      ? null
      : row.type === 'model'
        ? checkCustomFieldModel(row.value).host
        : row.type === 'link'
          ? checkCustomFieldLink(row.value).host
          : null

    return (
      <Input
        fullWidth
        label={VALUE_LABEL[row.type]}
        placeholder={VALUE_PLACEHOLDER[row.type]}
        value={row.value ?? ''}
        onChange={(v: string) => patch(row._key, { value: v })}
        error={!!error}
        helperText={
          error ??
          // The host, shown back to the brand — the same string the certificate shows
          // the reader beside the link, so what they are publishing is not a surprise.
          (host
            ? row.type === 'model'
              ? // Not "beside this link": nothing is shown beside a model panel. What the
                // brand needs confirmed is which HOST their customers' phones will fetch
                // a model from, since that request is made eagerly by every scanner.
                `Customers load this model from “${host}”.`
              : `Readers see “${host}” beside this link.`
            : undefined)
        }
      />
    )
  }

  return (
    <Card title="Custom fields" info>
      <Stack spacing={1} sx={{ mb: 3 }}>
        <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          Extra attributes shown on the certificate. Imported integration fields start
          hidden. Turn on the ones you want customers to see.
        </Typography>
      </Stack>

      {rows.length === 0 && (
        <Typography variant="body3" color="text.secondary" sx={{ mb: 3 }}>
          No custom fields yet.
        </Typography>
      )}

      <Stack spacing={2}>
        {rows.map((row, index) => (
          <Box
            key={row._key}
            sx={{
              border: (theme) => `1px solid ${theme.palette.divider}`,
              borderRadius: '12px',
              p: 2,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box sx={{ flex: 1 }}>
                <Input
                  fullWidth
                  placeholder="Label"
                  value={row.label}
                  onChange={(v: string) => patch(row._key, { label: v })}
                />
              </Box>
              <Box sx={{ width: 160, flexShrink: 0 }}>
                <SelectField
                  options={TYPE_OPTIONS}
                  value={row.type}
                  onChange={(v) =>
                    patch(row._key, { type: v as ProductCustomFieldType })
                  }
                />
              </Box>
              {/* Row controls: uniform 40×40 buttons, flush at input height. */}
              <Stack direction="row" spacing={1.5} flexShrink={0}>
                <VisibilityToggle
                  visible={row.visible}
                  onToggle={() => patch(row._key, { visible: !row.visible })}
                />
                <IconButton disabled={index === 0} onClick={() => move(index, -1)} sx={ACTION_BTN_SX}>
                  <ChevronUpIcon strokeWidth={1} size={18} />
                </IconButton>
                <IconButton
                  disabled={index === rows.length - 1}
                  onClick={() => move(index, 1)}
                  sx={ACTION_BTN_SX}
                >
                  <ChevronDownIcon strokeWidth={1} size={18} />
                </IconButton>
                <IconButton
                  onClick={() => remove(row._key)}
                  sx={{
                    ...ACTION_BTN_SX,
                    '&:hover': { backgroundColor: 'action.hover', color: 'error.main' },
                  }}
                >
                  <Trash2Icon strokeWidth={1} size={18} />
                </IconButton>
              </Stack>
            </Stack>

            <Box sx={{ mt: 2 }}>{renderValueControl(row)}</Box>
          </Box>
        ))}
      </Stack>

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 3 }}>
        <Button
          variant="tertiary"
          disableElevation
          startIcon={<PlusIcon strokeWidth={1} size={16} />}
          onClick={add}
        >
          Add field
        </Button>
        {/* A disabled Save has to say why (§46). Dirty-vs-pristine needs no tooltip —
            nothing is pending — but "you have unsaved work the API will refuse" does. */}
        <Tooltip title={dirty && blocked ? blockedReason : ''}>
          <span>
            <Button
              variant="primary"
              disableElevation
              disabled={!dirty || blocked}
              loading={isPending}
              onClick={handleSave}
            >
              Save custom fields
            </Button>
          </span>
        </Tooltip>
      </Stack>
    </Card>
  )
}

function serialize(rows: Row[]): string {
  return rows
    .map((r, i) => `${i}:${r.label}:${r.type}:${r.value ?? ''}:${r.visible ? 1 : 0}`)
    .join('|')
}
